import { ApiError } from "./apiError";
import axios, { AxiosError } from 'axios';
import { ServiceResponse } from "./serviceResponse";
import { apiService } from './apiService';
import { Acetylene, updateAcetylene } from "../dtos/Acetylene";

/**
 * Manejador de errores centralizado.
 * Convierte un error (potencialmente de Axios) en nuestro objeto ApiError estandarizado.
 * @param err - El error capturado en el bloque catch.
 * @returns Un objeto ApiError.
 */
const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    // Si el backend envía un objeto de error con 'message', úsalo.
    const errorMessage = error.response?.data?.message || 'Ocurrió un error en el servidor.';
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  // Para errores que no son de Axios (ej. de red, de código, etc.)
  return {
    message: 'Ocurrió un error inesperado. Revisa tu conexión de red.',
  };
};

/**
 * Obtiene la lista completa de acetilenos no consolidados.
 */

const getAll = async (): Promise<ServiceResponse<Acetylene[]>> => {
  try {
    const acetylene = await apiService.get<Acetylene[]>('/api/Acetileno/getAcetilenoConsolidado');
    return { data: acetylene, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};
const getAcetilenoBySucursal = async (id: number): Promise<ServiceResponse<Acetylene[]>> => {
  try {
    const acetylene = await apiService.get<Acetylene[]>(`/api/Acetileno/getAcetilenoConsolidado/${id}`);
    return { data: acetylene, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};
const updateReport = async (id: String) => {
  try {
    const updatedUser = await apiService.patch(`/api/Acetileno/updateAcetilenoStatus/${id}`, 4
    );
    return { data: updatedUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};
const updateDelete = async (id: String) => {
  try {
    const updatedUser = await apiService.patch(`/api/Acetileno/updateAcetilenoStatus/${id}`, 3);
    return { data: updatedUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

// types opcionales para claridad
export type GetAcetilenoFilters = {
  sucursalId?: number;         // int (query)
  fechainicio?: string;        // 'YYYY-MM-DD'
  fechaFin?: string;           // 'YYYY-MM-DD'
  usuario?: string;            // login/nombre
};

// GET /api/Acetileno/getAcetilenoConsolidadoByFilters?sucursalId=..&fechainicio=..&fechaFin=..&usuario=..
const getAcetilenoConsolidadoByFilters = async (
  filters: GetAcetilenoFilters
): Promise<ServiceResponse<Acetylene[]>> => {
  try {
    const params = new URLSearchParams();

    if (typeof filters.sucursalId === 'number' && !Number.isNaN(filters.sucursalId)) {
      params.append('sucursalId', String(filters.sucursalId));
    }
    if (filters.fechainicio) params.append('fechainicio', filters.fechainicio);
    if (filters.fechaFin)    params.append('fechaFin', filters.fechaFin);
    if (filters.usuario && filters.usuario.trim().length > 0) {
      params.append('usuario', filters.usuario.trim());
    }

    const url =
      '/api/Acetileno/getAcetilenoConsolidadoByFilters' +
      (params.toString() ? `?${params.toString()}` : '');

    const data = await apiService.get<Acetylene[]>(url);
    return { data, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

export const acetilenoService = {
  getAll,
  getAcetilenoBySucursal,
  getAcetilenoConsolidadoByFilters,
  updateDelete,
  updateReport
};